<template>
  <div>
    <loading-spinner v-if="loading"></loading-spinner>
    <template v-else>
      <v-row dense>
        <v-col cols="12">
          <v-btn text @click="goBack">
            <v-icon class="mr-2">keyboard_arrow_left</v-icon>
            Go Back
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-text class="py-0">
              <v-row dense>
                <v-col>
                  <v-card-title class="px-0">{{ customer.name }}</v-card-title>
                </v-col>
                <v-spacer> </v-spacer>
                <v-col cols="auto" class="my-auto">
                  <v-btn
                    color="secondary"
                    :to="`/reporting?customerId=${customer.id}`"
                  >
                    Reporting
                  </v-btn>
                </v-col>
                <v-col
                  cols="auto"
                  class="my-auto"
                  v-if="
                    isSuperAdmin &&
                      customer.primaryContact &&
                      customer.primaryContact.email
                  "
                >
                  <v-btn color="accent" @click="runAs">
                    Run As
                  </v-btn>
                </v-col>
                <v-col cols="auto" class="my-auto" v-if="isSuperAdmin">
                  <v-btn
                    color="error"
                    @click="deleteCustomer"
                    :loading="deleting"
                  >
                    Delete
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider></v-divider>
            <template v-if="customer.primaryContact">
              <v-card-text class="py-0 secondary--text">
                <v-row dense>
                  <v-col>
                    <span class="font-weight-bold subtitle-1">
                      Contact:
                    </span>
                    {{ customer.primaryContact.fullName }}
                  </v-col>
                  <v-col>
                    <span class="font-weight-bold subtitle-1">
                      Email:
                    </span>
                    {{ customer.primaryContact.email }}
                  </v-col>
                  <v-col>
                    <span class="font-weight-bold subtitle-1">
                      Phone:
                    </span>
                    {{ customer.primaryContact.phoneNumber }}
                  </v-col>
                </v-row>
              </v-card-text>
              <v-divider></v-divider>
            </template>
            <v-card-text class="py-0">
              <v-row>
                <report-overall-card
                  label="Total Users"
                  :value="customer.userCount.value | no_decimal"
                  flat
                >
                  <template v-slot:valueExtension>
                    <compare-text
                      align-end
                      ignore-margin
                      :old-value="customer.userCount.compare"
                      :new-value="customer.userCount.value"
                      :timeframeId="1"
                    >
                    </compare-text>
                  </template>
                  <div class="caption lighttext--text">
                    Created {{ customer.createdDate | longDate }}
                  </div>
                </report-overall-card>
                <v-divider vertical></v-divider>
                <report-overall-card
                  label="Avg Participation"
                  :value="
                    $options.filters.no_decimal(
                      customer.userRecords.value.completion
                    ) + '%'
                  "
                  :color="
                    $coreScores.getColor(
                      $coreScores.reverseFormat(
                        customer.userRecords.value.completion
                      )
                    )
                  "
                  flat
                >
                  <template v-slot:valueExtension>
                    <compare-text
                      align-end
                      ignore-margin
                      :old-value="customer.userRecords.compare.completion"
                      :new-value="customer.userRecords.value.completion"
                      :timeframeId="1"
                    >
                    </compare-text>
                  </template>
                  <div class="caption lighttext--text">
                    Overall company participation
                  </div>
                </report-overall-card>
                <v-divider vertical></v-divider>
                <report-overall-card
                  label="Overall Score"
                  :value="customer.score.value | no_decimal"
                  :color="
                    $coreScores.getColor(
                      $coreScores.reverseFormat(customer.score.value)
                    )
                  "
                  flat
                >
                  <template v-slot:valueExtension>
                    <compare-text
                      align-end
                      ignore-margin
                      :old-value="customer.score.compare"
                      :new-value="customer.score.value"
                      :timeframeId="1"
                    >
                    </compare-text>
                  </template>
                  <div class="caption lighttext--text">
                    This score is
                    <span
                      v-bind:style="{
                        color: $coreScores.getColor(
                          this.$coreScores.reverseFormat(customer.score.value)
                        )
                      }"
                      >{{
                        $coreScores.getTitle(
                          this.$coreScores.reverseFormat(customer.score.value)
                        )
                      }}</span
                    >
                  </div>
                </report-overall-card>
                <v-divider vertical></v-divider>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" v-if="customer.isPartner">
          <v-card>
            <v-card-title>Partner Management</v-card-title>
            <v-card-subtitle
              >View the customers of this partner</v-card-subtitle
            >
            <v-divider></v-divider>
            <v-card-text class="py-0">
              <v-row>
                <report-overall-card
                  label="Customers"
                  :value="customer.customerCount.value | no_decimal"
                  flat
                >
                  <template v-slot:valueExtension>
                    <compare-text
                      align-end
                      ignore-margin
                      :old-value="customer.customerCount.compare"
                      :new-value="customer.customerCount.value"
                      :timeframeId="1"
                    >
                    </compare-text>
                  </template>
                  <div class="caption lighttext--text">
                    Total users: {{ customer.customerUserCount }}
                  </div>
                </report-overall-card>
                <v-divider vertical></v-divider>
                <report-overall-card
                  label="Customers Participation"
                  :value="
                    $options.filters.no_decimal(
                      customer.customerUserRecords.value.completion
                    ) + '%'
                  "
                  :color="
                    $coreScores.getColor(
                      $coreScores.reverseFormat(
                        customer.customerUserRecords.value.completion
                      )
                    )
                  "
                  flat
                >
                  <template v-slot:valueExtension>
                    <compare-text
                      align-end
                      ignore-margin
                      :old-value="
                        customer.customerUserRecords.compare.completion
                      "
                      :new-value="customer.customerUserRecords.value.completion"
                      :timeframeId="1"
                    >
                    </compare-text>
                  </template>
                  <div class="caption lighttext--text">
                    Customers overall participation
                  </div>
                </report-overall-card>
                <v-divider vertical></v-divider>
                <report-overall-card
                  label="Customers Overall Score"
                  :value="customer.customerScore.value | no_decimal"
                  :color="
                    $coreScores.getColor(
                      $coreScores.reverseFormat(customer.customerScore.value)
                    )
                  "
                  flat
                >
                  <template v-slot:valueExtension>
                    <compare-text
                      align-end
                      ignore-margin
                      :old-value="customer.customerScore.compare"
                      :new-value="customer.customerScore.value"
                      :timeframeId="1"
                    >
                    </compare-text>
                  </template>
                  <div class="caption lighttext--text">
                    This score is
                    <span
                      v-bind:style="{
                        color: $coreScores.getColor(
                          this.$coreScores.reverseFormat(
                            customer.customerScore.value
                          )
                        )
                      }"
                    >
                      {{
                        $coreScores.getTitle(
                          this.$coreScores.reverseFormat(
                            customer.customerScore.value
                          )
                        )
                      }}
                    </span>
                  </div>
                </report-overall-card>
                <v-divider vertical></v-divider>
              </v-row>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-text>
              <customer-table
                :customers="partner_customers"
                :loading="loading"
              ></customer-table>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12">
          <v-card>
            <v-form ref="form" v-model="valid">
              <v-card-title>Admin Settings</v-card-title>
              <v-card-subtitle>
                We have additional options for this company
              </v-card-subtitle>
              <v-card-text class="py-0">
                <v-row dense>
                  <v-col cols="12" sm="6" md="4">
                    <member-autocomplete
                      v-model="primaryContact"
                      :multiple="false"
                      label="Primary Contact"
                      getter="customer/primary_contacts"
                      skip-get-data
                      :rules="[$rules.requiredArrayWithoutFalsy]"
                    >
                    </member-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <member-autocomplete
                      v-model="coreCoach"
                      :multiple="false"
                      label="CoreCoach"
                      getter="customer/core_coaches"
                      skip-get-data
                    >
                    </member-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" v-if="isSuperAdmin">
                    <v-select
                      v-model="customer.isPartner"
                      label="Partner"
                      :items="partnerItems"
                      item-text="name"
                      item-value="id"
                      outlined
                      required
                    >
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" v-if="isSuperAdmin">
                    <v-select
                      v-model="customer.locale"
                      label="Version"
                      :items="localeItems"
                      item-text="name"
                      item-value="id"
                      outlined
                      required
                    >
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" v-if="isSuperAdmin">
                    <v-select
                      v-model="customer.planTypeId"
                      label="Plan Type"
                      :items="company_plan_types"
                      item-text="name"
                      item-value="id"
                      outlined
                      required
                    >
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-select
                      v-model="customer.showManagers"
                      label="Show Managers"
                      :items="yesNoItems"
                      item-text="name"
                      item-value="id"
                      outlined
                    >
                    </v-select>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-form>
            <v-card-actions>
              <v-btn @click="save" :loading="saving">Update</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import MemberAutocomplete from "@/components/members/MemberAutocomplete";
import ReportOverallCard from "@/components/reporting/ReportOverallCard";
import CompareText from "@/components/reporting/CompareText";
import CustomerTable from "@/components/portal/CustomerTable";
import { mapGetters } from "vuex";
import { setRunAs } from "@/helpers/local";
export default {
  name: "CustomerDetails",
  components: {
    MemberAutocomplete,
    ReportOverallCard,
    CompareText,
    CustomerTable
  },
  props: { customerId: { type: [Number, String] } },
  data: () => ({
    loading: false,
    valid: false,
    saving: false,
    deleting: false,
    isPartner: false,
    partnerItems: [
      { id: false, name: "No" },
      { id: true, name: "Yes" }
    ],
    localeItems: [
      { id: null, name: "Regular" },
      { id: "table_group", name: "Table Group" }
    ],
    yesNoItems: [
      { id: true, name: "Yes" },
      { id: false, name: "No" }
    ]
  }),
  computed: {
    isSuperAdmin() {
      return this.$auth.isSuperAdmin();
    },
    primaryContact: {
      get() {
        if (!this.customer.primaryContact) {
          return [];
        }
        return [this.customer.primaryContact.id];
      },
      set(val) {
        this.customer.primaryContact = this.primary_contacts.find(
          x => x.id == (val || [""])[0]
        );
      }
    },
    coreCoach: {
      get() {
        if (!this.customer.coreCoach) {
          return [];
        }
        return [this.customer.coreCoach.id];
      },
      set(val) {
        this.customer.coreCoach = this.core_coaches.find(
          x => x.id == (val || [""])[0]
        );
      }
    },
    ...mapGetters({
      customer: "customer/customer",
      primary_contacts: "customer/primary_contacts",
      core_coaches: "customer/core_coaches",
      partner_customers: "customer/partner_customers",
      company_plan_types: "lookup/company_plan_types"
    })
  },
  created() {
    this.loadData();
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    loadData() {
      this.loading = true;
      let callArray = [
        this.$store.dispatch("customer/getCustomer", this.customerId),
        this.$store.dispatch("customer/getPrimaryContacts", this.customerId),
        this.$store.dispatch("customer/getCoreCoaches", this.customerId)
      ];
      if (this.isSuperAdmin) {
        callArray.push(
          this.$store.dispatch("customer/getPartnerCustomers", this.customerId)
        );
      }
      Promise.all(callArray)
        .then(() => {
          this.isPartner = this.customer.isPartner;
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while loading customers."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async save() {
      if (this.$refs.form.validate()) {
        if (
          !this.isPartner ||
          this.customer.isPartner ||
          (await this.$root.$confirm(
            "Remove Partner",
            "Are you sure you want to remove this customer from being a partner? This will remove them as partner from all customers.",
            { color: "red" }
          ))
        ) {
          this.saving = true;
          this.$store
            .dispatch("customer/updateCustomer", this.customer)
            .then(() => {
              this.$emit("save");
              this.$root.$snackbarSuccess("Admin Settings saved");
            })
            .catch(error => {
              this.logError(error);
              this.$root.$alert(
                "Error Occurred",
                "An error occured while saving customer."
              );
            })
            .finally(() => {
              this.saving = false;
            });
        }
      }
    },
    runAs() {
      setRunAs(this.customer.primaryContact.email);
    },
    async deleteCustomer() {
      if (
        await this.$root.$confirm(
          "Delete Customer",
          "Are you sure you want to remove this customer? This will remove access for all of their users.",
          { color: "red" }
        )
      ) {
        this.deleting = true;
        this.$store
          .dispatch("customer/deleteCustomer", this.customer.id)
          .then(() => {
            this.$emit("delete");
            this.$root.$snackbarSuccess("Customer deleted");
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occured while deleting customer."
            );
          })
          .finally(() => {
            this.deleting = false;
          });
      }
    }
  }
};
</script>
